import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, LogOut, Eye, EyeOff, X, KeyRound, Check, AlertCircle } from "lucide-react";

// Admin-only Keap API key rotation panel
function KeapKeyPanel() {
  const [status, setStatus] = useState({ configured: false, masked: null });
  const [newKey, setNewKey] = useState("");
  const [reveal, setReveal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { ok: bool, message: string }

  const load = async () => {
    try {
      const r = await api.get("/admin/keap-key");
      setStatus(r.data || { configured: false, masked: null });
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!newKey.trim()) return;
    setSaving(true);
    setResult(null);
    try {
      const r = await api.put("/admin/keap-key", { api_key: newKey.trim() });
      setResult({ ok: true, message: r.data?.message || "Key updated." });
      setNewKey("");
      await load();
    } catch (err) {
      setResult({ ok: false, message: formatApiErrorDetail(err?.response?.data?.detail) || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <details className="mb-8 border border-rule bg-card" data-testid="admin-keap-key-panel">
      <summary className="cursor-pointer list-none p-5 flex items-center justify-between hover:bg-parchment/40">
        <div className="flex items-center gap-3">
          <KeyRound size={16} className="text-eucalypt" />
          <div>
            <p className="specimen-label text-muted">Keap · API Key</p>
            <p className="font-mono text-sm mt-1">
              {status.configured ? (
                <span className="text-ink">{status.masked}</span>
              ) : (
                <span className="text-terracotta">Not configured</span>
              )}
            </p>
          </div>
        </div>
        <span className="specimen-label text-eucalypt">Rotate</span>
      </summary>
      <div className="border-t border-rule p-5">
        <p className="text-sm text-muted mb-4 leading-relaxed">
          Paste a new Service Account Key (starts with <code className="font-mono text-xs">KeapAK-</code>).
          It&rsquo;s validated against Keap before saving. The old key stops working immediately.
        </p>
        <form onSubmit={save} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <input
              type={reveal ? "text" : "password"}
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="KeapAK-…"
              required
              className="w-full field-input font-mono text-sm pr-12"
              data-testid="keap-key-input"
              autoComplete="off"
              spellCheck="false"
            />
            <button
              type="button"
              onClick={() => setReveal((r) => !r)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-ink"
              data-testid="keap-key-reveal"
              tabIndex={-1}
            >
              {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <button
            type="submit"
            disabled={saving || !newKey.trim()}
            className="bg-eucalypt text-chalk px-5 py-3 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-eucalypt-dark disabled:opacity-60 whitespace-nowrap"
            data-testid="keap-key-save"
          >
            {saving ? "Validating…" : "Validate & Save"}
          </button>
        </form>
        {result && (
          <p
            className={`mt-4 text-sm flex items-start gap-2 ${result.ok ? "text-eucalypt" : "text-terracotta"}`}
            data-testid="keap-key-result"
          >
            {result.ok ? <Check size={16} className="mt-0.5 shrink-0" /> : <AlertCircle size={16} className="mt-0.5 shrink-0" />}
            <span>{result.message}</span>
          </p>
        )}
      </div>
    </details>
  );
}

// Small SVG sparkline showing signups per day for the last N days
function SignupSparkline({ subscribers, days = 30 }) {
  const now = new Date();
  const buckets = Array.from({ length: days }, (_, i) => {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - (days - 1 - i));
    return { date: d, count: 0 };
  });
  const startTs = buckets[0].date.getTime();
  for (const s of subscribers) {
    const t = new Date(s.created_at);
    if (isNaN(t)) continue;
    const day = new Date(t);
    day.setHours(0, 0, 0, 0);
    const idx = Math.floor((day.getTime() - startTs) / 86400000);
    if (idx >= 0 && idx < days) buckets[idx].count += 1;
  }
  const total = subscribers.length;
  const last7 = buckets.slice(-7).reduce((a, b) => a + b.count, 0);
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const w = 560;
  const h = 60;
  const gap = 2;
  const barW = (w - gap * (days - 1)) / days;
  const fmt = (d) => d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
  return (
    <div className="mb-8 border border-rule bg-card p-5" data-testid="subscribers-sparkline">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
        <div>
          <p className="specimen-label text-muted">Signups · last {days} days</p>
          <p className="font-serif text-3xl md:text-4xl leading-none mt-2">
            {total} <span className="text-muted text-lg font-mono tracking-widest">total</span>
          </p>
        </div>
        <div className="text-right">
          <p className="specimen-label text-muted">Last 7 days</p>
          <p className="font-serif text-2xl mt-2 text-eucalypt">
            {last7 > 0 ? "+" : ""}{last7}
          </p>
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14" preserveAspectRatio="none" role="img" aria-label={`Signups per day for the last ${days} days`}>
        {buckets.map((b, i) => {
          const bh = (b.count / max) * (h - 4);
          const x = i * (barW + gap);
          const y = h - bh;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={Math.max(bh, b.count > 0 ? 2 : 1)}
                fill={b.count > 0 ? "rgb(var(--eucalypt))" : "rgb(var(--rule))"}
                data-testid={`sparkline-bar-${i}`}
              >
                <title>{`${fmt(b.date)} — ${b.count} signup${b.count === 1 ? "" : "s"}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-muted">
        <span>{fmt(buckets[0].date)}</span>
        <span>{fmt(buckets[buckets.length - 1].date)}</span>
      </div>
    </div>
  );
}

const empty = {
  title: "",
  category: "Article",
  summary: "",
  body: "",
  author: "SeedKeepers",
  cover_image: "",
  tags: "",
  published: true,
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // resource being edited
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("resources");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [enquiryFilter, setEnquiryFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, l, s, e] = await Promise.all([
        api.get("/resources/admin"),
        api.get("/contact"),
        api.get("/leads"),
        api.get("/subscribers"),
        api.get("/stewardship-enquiries"),
      ]);
      setItems(r.data || []);
      setContacts(c.data || []);
      setLeads(l.data || []);
      setSubscribers(s.data || []);
      setEnquiries(e.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing("new");
    setForm(empty);
    setError("");
  };

  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      title: r.title,
      category: r.category,
      summary: r.summary,
      body: r.body,
      author: r.author || "SeedKeepers",
      cover_image: r.cover_image || "",
      tags: (r.tags || []).join(", "),
      published: r.published,
    });
    setError("");
  };

  const close = () => { setEditing(null); setForm(empty); setError(""); };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      cover_image: form.cover_image || null,
    };
    try {
      if (editing === "new") {
        await api.post("/resources", payload);
      } else {
        await api.put(`/resources/${editing}`, payload);
      }
      await load();
      close();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await api.delete(`/resources/${r.id}`);
      await load();
    } catch (err) {
      alert(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
  };

  const togglePublish = async (r) => {
    try {
      await api.put(`/resources/${r.id}`, { published: !r.published });
      await load();
    } catch (err) {
      alert(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-[80vh]" data-testid="page-admin-dashboard">
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="specimen-label text-chalk/60 mb-2">Admin · Console</p>
            <h1 className="font-serif text-4xl md:text-5xl">Welcome, {user?.name || "Admin"}.</h1>
            <p className="text-chalk/70 text-sm mt-2">Signed in as {user?.email}</p>
          </div>
          <div className="flex gap-3">
            <Link to="/" className="border border-chalk/30 px-5 py-3 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-chalk hover:text-bark transition-colors" data-testid="admin-view-site">
              View site
            </Link>
            <button
              onClick={async () => { await logout(); nav("/admin/login", { replace: true }); }}
              className="inline-flex items-center gap-2 bg-ochre text-bark px-5 py-3 font-mono text-[11px] tracking-[0.2em] uppercase hover:opacity-90 transition-opacity"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
          <div className="flex gap-2 mb-8 flex-wrap" data-testid="admin-tabs">
            {["resources", "contacts", "leads", "subscribers", "enquiries"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`tab-${t}`}
                className={`text-[11px] tracking-[0.22em] uppercase font-mono px-5 py-2 border ${
                  tab === t ? "bg-ink text-chalk border-ink" : "border-rule hover:border-ink"
                }`}
              >
                {t === "enquiries" ? "Stewardship Enquiries" : t}
                {t === "contacts" && contacts.length > 0 && ` (${contacts.length})`}
                {t === "leads" && leads.length > 0 && ` (${leads.length})`}
                {t === "subscribers" && subscribers.length > 0 && ` (${subscribers.length})`}
                {t === "enquiries" && enquiries.length > 0 && ` (${enquiries.length})`}
              </button>
            ))}
          </div>

          {tab === "resources" && (
            <>
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-3xl">Resources</h2>
                <button
                  onClick={openCreate}
                  className="inline-flex items-center gap-2 bg-eucalypt text-chalk px-5 py-3 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-eucalypt-dark transition-colors"
                  data-testid="admin-new-resource"
                >
                  <Plus size={14} /> New Resource
                </button>
              </div>

              {loading ? (
                <p className="specimen-label">Loading…</p>
              ) : (
                <ul className="divide-y divide-rule border-y border-rule" data-testid="admin-resources-list">
                  {items.map((r, i) => (
                    <li key={r.id} className="py-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center" data-testid={`admin-resource-${i}`}>
                      <div className="md:col-span-2 specimen-label">
                        {r.category}<br />
                        {new Date(r.updated_at).toLocaleDateString("en-AU")}
                      </div>
                      <div className="md:col-span-7">
                        <p className="font-serif text-xl md:text-2xl">{r.title}</p>
                        <p className="text-sm text-muted mt-1 line-clamp-1">{r.summary}</p>
                      </div>
                      <div className="md:col-span-1">
                        {r.published ? (
                          <span className="text-[10px] tracking-widest uppercase text-eucalypt border border-eucalypt px-2 py-0.5">Live</span>
                        ) : (
                          <span className="text-[10px] tracking-widest uppercase text-muted border border-muted px-2 py-0.5">Draft</span>
                        )}
                      </div>
                      <div className="md:col-span-2 flex gap-2 justify-end">
                        <button onClick={() => togglePublish(r)} className="p-2 border border-rule hover:border-ink" data-testid={`admin-toggle-${i}`} title={r.published ? "Unpublish" : "Publish"}>
                          {r.published ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button onClick={() => openEdit(r)} className="p-2 border border-rule hover:border-ink" data-testid={`admin-edit-${i}`}>
                          <Edit2 size={14} />
                        </button>
                        <button onClick={() => remove(r)} className="p-2 border border-rule hover:border-terracotta hover:text-terracotta" data-testid={`admin-delete-${i}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {tab === "contacts" && (
            <>
              <h2 className="font-serif text-3xl mb-6">Contact submissions</h2>
              {contacts.length === 0 ? (
                <p className="specimen-label">No messages yet.</p>
              ) : (
                <ul className="divide-y divide-rule border-y border-rule" data-testid="admin-contacts-list">
                  {contacts.map((c, i) => (
                    <li key={c.id} className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4" data-testid={`admin-contact-${i}`}>
                      <div className="md:col-span-3 specimen-label">
                        {new Date(c.created_at).toLocaleString("en-AU")}<br />
                        {c.audience || "—"}
                      </div>
                      <div className="md:col-span-9">
                        <p className="font-serif text-2xl">{c.name} <span className="text-muted text-base">· {c.email}</span></p>
                        {c.organisation && <p className="text-sm text-muted">{c.organisation}</p>}
                        <p className="mt-3 text-sm leading-relaxed whitespace-pre-line">{c.message}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {tab === "leads" && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-3xl">Download leads</h2>
                <a
                  href={`data:text/csv;charset=utf-8,${encodeURIComponent("Date,Name,Email,Organisation,Role,Asset\n" + leads.map(l => `${l.created_at},${l.name},${l.email},${l.organisation || ""},${l.role || ""},${l.asset}`).join("\n"))}`}
                  download="seedkeepers-leads.csv"
                  className="link-mono text-eucalypt"
                  data-testid="leads-export"
                >
                  Export CSV
                </a>
              </div>
              {leads.length === 0 ? (
                <p className="specimen-label">No leads yet.</p>
              ) : (
                <ul className="divide-y divide-rule border-y border-rule" data-testid="admin-leads-list">
                  {leads.map((l, i) => (
                    <li key={l.id} className="py-5 grid grid-cols-1 md:grid-cols-12 gap-4" data-testid={`admin-lead-${i}`}>
                      <div className="md:col-span-3 specimen-label">
                        {new Date(l.created_at).toLocaleString("en-AU")}<br />
                        <span className="text-ochre2">{l.asset}</span>
                      </div>
                      <div className="md:col-span-9">
                        <p className="font-serif text-xl md:text-2xl">{l.name} <span className="text-muted text-base">· {l.email}</span></p>
                        <p className="text-sm text-muted mt-1">
                          {[l.organisation, l.role].filter(Boolean).join(" · ") || "—"}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {tab === "subscribers" && (
            <>
              <KeapKeyPanel />

              {(() => {
                const sources = Array.from(new Set(subscribers.map((s) => s.source || "").filter(Boolean))).sort();
                const filtered = sourceFilter === "all"
                  ? subscribers
                  : subscribers.filter((s) => (s.source || "") === sourceFilter);
                const prettySource = (v) => v.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                const filterLabel = sourceFilter === "all" ? "all sources" : prettySource(sourceFilter);
                return (
                  <>
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                      <div>
                        <h2 className="font-serif text-3xl">Newsletter subscribers</h2>
                        <p className="text-sm text-muted mt-1">
                          Live from the site. Also synced to Keap with the <em>Newsletter Subscriber</em> tag + source-specific tags.
                        </p>
                      </div>
                      <div className="flex items-center gap-4 flex-wrap">
                        <label className="flex items-center gap-2 specimen-label text-muted">
                          Source
                          <select
                            value={sourceFilter}
                            onChange={(e) => setSourceFilter(e.target.value)}
                            data-testid="subscribers-source-filter"
                            className="field-input bg-transparent py-2 pl-3 pr-8 text-sm normal-case tracking-normal font-sans"
                          >
                            <option value="all">All sources ({subscribers.length})</option>
                            {sources.map((src) => {
                              const c = subscribers.filter((s) => (s.source || "") === src).length;
                              return (
                                <option key={src} value={src}>
                                  {prettySource(src)} ({c})
                                </option>
                              );
                            })}
                          </select>
                        </label>
                        <button
                          onClick={() => {
                            const esc = (v) => {
                              const s = (v ?? "").toString();
                              return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                            };
                            const header = ["Date", "Email", "Source"].join(",");
                            const rows = filtered.map((s) =>
                              [s.created_at, s.email, s.source].map(esc).join(",")
                            );
                            const csv = [header, ...rows].join("\n");
                            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            const tag = sourceFilter === "all" ? "all" : sourceFilter;
                            a.download = `seedkeepers-subscribers-${tag}-${new Date().toISOString().slice(0, 10)}.csv`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          }}
                          disabled={filtered.length === 0}
                          className="link-mono text-eucalypt disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid="subscribers-export"
                        >
                          Export CSV
                        </button>
                      </div>
                    </div>

                    <SignupSparkline subscribers={filtered} />

                    {filtered.length === 0 ? (
                      <p className="specimen-label" data-testid="subscribers-empty">
                        {subscribers.length === 0 ? "No subscribers yet." : `No subscribers matching ${filterLabel}.`}
                      </p>
                    ) : (
                      <ul className="divide-y divide-rule border-y border-rule" data-testid="admin-subscribers-list">
                        {filtered.map((s, i) => (
                          <li key={s.id} className="py-4 grid grid-cols-1 md:grid-cols-12 gap-4 items-center" data-testid={`admin-subscriber-${i}`}>
                            <div className="md:col-span-3 specimen-label">
                              {new Date(s.created_at).toLocaleString("en-AU")}
                            </div>
                            <div className="md:col-span-5">
                              <p className="font-serif text-lg md:text-xl break-all">{s.email}</p>
                            </div>
                            <div className="md:col-span-3 text-right md:text-left">
                              <span className="text-[10px] tracking-widest uppercase text-ochre2 border border-ochre2/60 px-2 py-0.5">
                                {s.source || "—"}
                              </span>
                            </div>
                            <div className="md:col-span-1 flex justify-end">
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Unsubscribe ${s.email}? This removes them here and strips the newsletter tag in Keap.`)) return;
                                  try {
                                    await api.delete(`/subscribers/${s.id}`);
                                    setSubscribers((prev) => prev.filter((x) => x.id !== s.id));
                                  } catch (err) {
                                    alert(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
                                  }
                                }}
                                className="p-2 border border-rule hover:border-terracotta hover:text-terracotta transition-colors"
                                title="Unsubscribe"
                                data-testid={`admin-subscriber-delete-${i}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                );
              })()}
            </>
          )}

          {tab === "enquiries" && (
            <>
              {(() => {
                const INTEREST_LABELS = {
                  "collection-training": "Collection training",
                  "seed-bank": "Seed bank",
                  "digital-tools": "Digital tools",
                  "restoration-supply": "Restoration supply",
                  "governance": "Governance",
                  "funding-partnerships": "Funding & partnerships",
                  "other": "Other",
                };
                const allInterests = Array.from(
                  new Set(enquiries.flatMap((e) => e.interests || []))
                ).sort();
                const filtered =
                  enquiryFilter === "all"
                    ? enquiries
                    : enquiries.filter((e) => (e.interests || []).includes(enquiryFilter));
                return (
                  <>
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                      <div>
                        <h2 className="font-serif text-3xl">Stewardship Enquiries</h2>
                        <p className="text-sm text-muted mt-1 max-w-2xl leading-relaxed">
                          Live from <code className="font-mono text-xs">/community-led-seed-collection</code>.
                          Each enquiry is also pushed to Keap with the{" "}
                          <em>Community-Led Seed Collection</em> tag so your Keap automation can email you.
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <label className="flex items-center gap-2 specimen-label text-muted">
                          Interest
                          <select
                            value={enquiryFilter}
                            onChange={(e) => setEnquiryFilter(e.target.value)}
                            data-testid="enquiries-interest-filter"
                            className="field-input bg-transparent py-2 pl-3 pr-8 text-sm normal-case tracking-normal font-sans"
                          >
                            <option value="all">All interests ({enquiries.length})</option>
                            {allInterests.map((i) => {
                              const c = enquiries.filter((e) => (e.interests || []).includes(i)).length;
                              return (
                                <option key={i} value={i}>
                                  {INTEREST_LABELS[i] || i} ({c})
                                </option>
                              );
                            })}
                          </select>
                        </label>
                        <button
                          onClick={() => {
                            const esc = (v) => {
                              const s = (v ?? "").toString();
                              return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                            };
                            const header = [
                              "Date",
                              "Name",
                              "Organisation",
                              "Email",
                              "Phone",
                              "Interests",
                              "Prefers Phone",
                              "Message",
                            ].join(",");
                            const rows = filtered.map((e) =>
                              [
                                e.created_at,
                                e.name,
                                e.organisation,
                                e.email || "",
                                e.phone || "",
                                (e.interests || []).join("|"),
                                e.prefer_phone ? "yes" : "no",
                                e.message,
                              ]
                                .map(esc)
                                .join(",")
                            );
                            const csv = [header, ...rows].join("\n");
                            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            const tag = enquiryFilter === "all" ? "all" : enquiryFilter;
                            a.download = `seedkeepers-enquiries-${tag}-${new Date().toISOString().slice(0, 10)}.csv`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            URL.revokeObjectURL(url);
                          }}
                          disabled={filtered.length === 0}
                          className="link-mono text-eucalypt disabled:opacity-40"
                          data-testid="enquiries-export"
                        >
                          Export CSV
                        </button>
                      </div>
                    </div>

                    <div
                      className="mb-6 rounded-2xl border border-ochre2/30 bg-ochre2/5 px-5 py-4 text-xs md:text-sm text-ink/80 leading-relaxed max-w-3xl"
                      data-testid="enquiries-keap-hint"
                    >
                      <strong className="font-mono text-[11px] tracking-widest uppercase block mb-1">
                        One-time Keap setup
                      </strong>
                      In Keap, create an <em>Easy Automation</em> with trigger{" "}
                      <strong>“Tag applied: Community-Led Seed Collection”</strong> → action{" "}
                      <strong>“Send email to william@seedkeepers.com.au”</strong>. Interest-specific
                      sub-tags (<code className="font-mono text-xs">Interest: Collection Training</code>{" "}
                      etc.) are also applied so you can route by topic.
                    </div>

                    {loading ? (
                      <p className="specimen-label">Loading…</p>
                    ) : filtered.length === 0 ? (
                      <p className="specimen-label" data-testid="enquiries-empty">
                        {enquiries.length === 0
                          ? "No enquiries yet."
                          : "No enquiries match this interest."}
                      </p>
                    ) : (
                      <ul
                        className="divide-y divide-rule border-y border-rule"
                        data-testid="admin-enquiries-list"
                      >
                        {filtered.map((e, i) => (
                          <li
                            key={e.id}
                            className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4"
                            data-testid={`admin-enquiry-${i}`}
                          >
                            <div className="md:col-span-3 specimen-label">
                              {new Date(e.created_at).toLocaleString("en-AU")}
                              {e.prefer_phone && (
                                <>
                                  <br />
                                  <span className="text-ochre2 normal-case tracking-normal font-sans text-xs mt-1 inline-block">
                                    Prefers phone
                                  </span>
                                </>
                              )}
                            </div>
                            <div className="md:col-span-9">
                              <p className="font-serif text-xl md:text-2xl">
                                {e.name}
                                {(e.email || e.phone) && (
                                  <span className="text-muted text-base">
                                    {" · "}
                                    {e.email || e.phone}
                                  </span>
                                )}
                              </p>
                              {e.organisation && (
                                <p className="text-sm text-muted">{e.organisation}</p>
                              )}
                              {(e.interests || []).length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {e.interests.map((tag) => (
                                    <span
                                      key={tag}
                                      className="text-[10px] tracking-widest uppercase border border-eucalypt/40 text-eucalypt px-2 py-0.5 rounded-full"
                                    >
                                      {INTEREST_LABELS[tag] || tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {e.message && (
                                <p className="mt-3 text-sm leading-relaxed whitespace-pre-line">
                                  {e.message}
                                </p>
                              )}
                              <div className="mt-3 flex gap-4 text-xs">
                                {e.email && (
                                  <a
                                    href={`mailto:${e.email}?subject=${encodeURIComponent(
                                      "Re: Your SeedKeepers enquiry"
                                    )}`}
                                    className="link-mono text-eucalypt"
                                    data-testid={`enquiry-reply-${i}`}
                                  >
                                    Reply by email
                                  </a>
                                )}
                                {e.phone && (
                                  <a
                                    href={`tel:${e.phone.replace(/\s+/g, "")}`}
                                    className="link-mono text-eucalypt"
                                    data-testid={`enquiry-call-${i}`}
                                  >
                                    Call
                                  </a>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      </section>

      {/* Edit / Create Drawer */}
      {editing && (
        <div className="fixed inset-0 bg-bark/60 z-50 flex items-start justify-center overflow-y-auto pt-10 pb-10 px-4" data-testid="admin-form-modal">
          <div className="bg-cream border border-ink w-full max-w-3xl">
            <div className="flex items-center justify-between p-5 border-b border-rule">
              <h3 className="font-serif text-2xl">{editing === "new" ? "New Resource" : "Edit Resource"}</h3>
              <button onClick={close} className="p-2 border border-rule" data-testid="admin-form-close"><X size={16} /></button>
            </div>
            <form onSubmit={save} className="p-6 md:p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="specimen-label">Title</label>
                  <input required className="field-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="form-title" />
                </div>
                <div>
                  <label className="specimen-label">Category</label>
                  <select className="field-input bg-transparent" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category">
                    {["White Paper", "Article", "Guide", "Framework", "Report", "Field Note"].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="specimen-label">Author</label>
                  <input className="field-input" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="form-author" />
                </div>
                <div className="md:col-span-2">
                  <label className="specimen-label">Cover image URL (optional)</label>
                  <input className="field-input" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="form-cover" />
                </div>
                <div className="md:col-span-2">
                  <label className="specimen-label">Tags (comma separated)</label>
                  <input className="field-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="form-tags" />
                </div>
                <div className="md:col-span-2">
                  <label className="specimen-label">Summary</label>
                  <textarea required rows={3} className="field-input resize-y" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="form-summary" />
                </div>
                <div className="md:col-span-2">
                  <label className="specimen-label">Body</label>
                  <textarea required rows={10} className="field-input resize-y" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="form-body" />
                </div>
                <div className="md:col-span-2">
                  <label className="inline-flex items-center gap-3 text-sm">
                    <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="form-published" />
                    <span className="specimen-label">Published</span>
                  </label>
                </div>
              </div>
              {error && <p className="text-terracotta text-sm font-mono" data-testid="form-error">{error}</p>}
              <div className="flex gap-3">
                <button type="submit" disabled={busy} className="bg-eucalypt text-chalk px-7 py-3 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-eucalypt-dark disabled:opacity-60" data-testid="form-save">
                  {busy ? "Saving…" : "Save"}
                </button>
                <button type="button" onClick={close} className="rounded-full border border-ink/40 px-7 py-3 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-ink hover:text-chalk hover:-translate-y-0.5 transition-all" data-testid="form-cancel">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
